// ============================================================================
// Client query + mutation hooks.
// ----------------------------------------------------------------------------
// Reads use the centralized keys (qk.clients.*) so the live-sync layer can
// invalidate them on a `client` change event. Mutations apply OPTIMISTIC
// updates so the originating device feels instant, then settle against the
// server response; the WebSocket echo to OTHER devices triggers their refetch.
// ============================================================================
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { clientsApi, qk } from "../api";
import type { Client, ClientInput } from "../api";

export function useClients(starred = false) {
  return useQuery({
    queryKey: qk.clients.list(starred),
    queryFn: ({ signal }) => clientsApi.list(starred, signal),
  });
}

export function useClient(id: number | undefined) {
  return useQuery({
    queryKey: qk.clients.detail(id ?? -1),
    queryFn: ({ signal }) => clientsApi.get(id as number, signal),
    enabled: id !== undefined && id >= 0,
  });
}

/**
 * Clients who have gone QUIET (Wave 6 inactivity flag) — last_active_at older
 * than the trainer's window (or never set). Hangs off `qk.clients.inactive`, so
 * the `client` live-sync invalidation refetches it when a client signs in /
 * self-logs (their last_active_at is touched + a `client` change broadcasts),
 * dropping them off the list without a reload. `enabled` lets callers gate it
 * on auth.
 */
export function useInactiveClients(enabled = true) {
  return useQuery({
    queryKey: qk.clients.inactive,
    queryFn: ({ signal }) => clientsApi.inactive(signal),
    enabled,
    // The flag is purely a nudge; a brief staleness is fine and the WS
    // invalidation refetches on every real change anyway.
    staleTime: 60 * 1000,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClientInput) => clientsApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.clients.root });
    },
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ClientInput }) =>
      clientsApi.update(id, body),
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: qk.clients.detail(id) });
      const prev = qc.getQueryData<Client>(qk.clients.detail(id));
      if (prev) {
        qc.setQueryData<Client>(qk.clients.detail(id), { ...prev, ...body });
      }
      return { prev, id };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.clients.detail(ctx.id), ctx.prev);
    },
    onSettled: (_data, _err, { id }) => {
      void qc.invalidateQueries({ queryKey: qk.clients.detail(id) });
      void qc.invalidateQueries({ queryKey: qk.clients.root });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => clientsApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.clients.root });
    },
  });
}

/** Toggle a client's star — optimistic flip across every cached client list. */
export function useStarClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => clientsApi.star(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: qk.clients.root });
      const snapshots = qc.getQueriesData<Client[]>({
        queryKey: qk.clients.root,
      });
      for (const [key, list] of snapshots) {
        if (!Array.isArray(list)) continue;
        qc.setQueryData<Client[]>(
          key,
          list.map((c) =>
            c.id === id ? { ...c, starred: c.starred ? 0 : 1 } : c,
          ),
        );
      }
      const prevDetail = qc.getQueryData<Client>(qk.clients.detail(id));
      if (prevDetail) {
        qc.setQueryData<Client>(qk.clients.detail(id), {
          ...prevDetail,
          starred: prevDetail.starred ? 0 : 1,
        });
      }
      return { snapshots, prevDetail, id };
    },
    onError: (_err, _id, ctx) => {
      ctx?.snapshots.forEach(([key, data]) => qc.setQueryData(key, data));
      if (ctx?.prevDetail) {
        qc.setQueryData(qk.clients.detail(ctx.id), ctx.prevDetail);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.clients.root });
    },
  });
}
